import { OrigamiResponse } from '@app/origami-api/services/base/service.base';
import { of, Observable, Subject } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { ErrorCode } from '@app/origami-api/constants/error-codes';
import { ActionSource } from '@app/origami-api/constants/action-source';

export interface AdditionalRequestOptions {
  useSimpleValidationParamsDelivery?: boolean;
  forceAdditionalRequest?: boolean;
  additionalRequest?: (params?: any) => Observable<OrigamiResponse<any>>;
  isSimpleAuthMethod?: boolean;
  //useSourceCredentials?: boolean;
  userName?: string;
  password?: string;
  proceedWithRequest?: boolean;
  source?: ActionSource;
  componentWithSpinner?: { isLoading?: boolean; signingSubject?: Subject<boolean> };
}

export interface RequestValidationConfig {
  additionalRequestOptions: AdditionalRequestOptions;
  modifiedParams: any; // call specific
  modifiedSilentErrors?: ErrorCode[];
  userName?: string;
  password?: string;
}

export interface SignOrDeclineRequestConfig {
  requestParams: any;
  additionalRequestOptions: AdditionalRequestOptions;
}

export abstract class RequestProxy<TParams, TResponseResult> {
  run(
    request: (params?: TParams, options?: AdditionalRequestOptions) => Observable<OrigamiResponse<TResponseResult>>,
    options?: AdditionalRequestOptions,
  ): Observable<OrigamiResponse<TResponseResult>> {
    return this.request(null, request, options);
  }

  protected abstract getParams(
    response: OrigamiResponse<TResponseResult>,
    options?: AdditionalRequestOptions,
  ): Observable<{ cancel: boolean; data: TParams }>;
  protected abstract needProxify(
    response: OrigamiResponse<TResponseResult>,
    options?: AdditionalRequestOptions,
  ): boolean;

  private request(
    params: TParams,
    request: (params?: TParams, options?: AdditionalRequestOptions) => Observable<OrigamiResponse<TResponseResult>>,
    options?: AdditionalRequestOptions,
  ): Observable<OrigamiResponse<TResponseResult>> {
    let additionalRequestOptions: AdditionalRequestOptions = options;
    if (additionalRequestOptions && additionalRequestOptions.isSimpleAuthMethod) {
      const standardResponse: OrigamiResponse<any> = {
        ErrorCode: ErrorCode.USER_VALIDATION_REQUIRED,
        Result: {},
      };
      const currentRequest = additionalRequestOptions.proceedWithRequest
        ? request(params, additionalRequestOptions)
        : of(standardResponse);
      return currentRequest.pipe(
        concatMap((response) => {
          if (this.needProxify(response, additionalRequestOptions)) {
            return this.getParams(response, additionalRequestOptions).pipe(
              concatMap((res) => {
                if (res.cancel) {
                  additionalRequestOptions.proceedWithRequest = false; // ??
                  return of(response);
                }
                additionalRequestOptions.proceedWithRequest = true;
                if (options?.componentWithSpinner) {
                  options.componentWithSpinner.isLoading = true;

                  options.componentWithSpinner.signingSubject && options.componentWithSpinner.signingSubject.next(true);
                }
                return this.request(res.data, request, additionalRequestOptions);
              }),
            );
          }
          return of(response);
        }),
      );
    } else {
      return request(params, additionalRequestOptions).pipe(
        concatMap((response) => {
          if (this.needProxify(response, additionalRequestOptions)) {
            return this.getParams(response, additionalRequestOptions).pipe(
              concatMap((res) => {
                if (res.cancel) {
                  return of(response);
                }
                if (options?.componentWithSpinner?.signingSubject) {
                  options.componentWithSpinner.signingSubject.next(true);
                }
                return this.request(res.data, request, options);
              }),
            );
          }
          return of(response);
        }),
      );
    }
  }
}
